/**
 * LottiePreview-Komponente fuer den Creator Studio
 *
 * Zeigt Lottie-Animationen mit interaktiven Steuerungselementen an.
 * Unterstuetzt Play/Pause bei Hover, Geschwindigkeitssteuerung und
 * responsive Groessenanpassung.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import type { AnimationElement } from '../config/elementTypes';

// ===== INTERFACES =====

export interface LottiePreviewProps {
  /** Lottie Animation JSON Daten */
  animationData: object;
  /** Animation wiederholen (Standard: true) */
  loop?: boolean;
  /** Automatisch abspielen (Standard: true) */
  autoplay?: boolean;
  /** Abspielgeschwindigkeit (Standard: 1) */
  speed?: number;
  /** Zusaetzliche CSS-Klassen */
  className?: string;
  /** Click-Handler fuer das Element */
  onClick?: () => void;
  /** Hover-Verhalten aktivieren (Play/Pause bei Hover) */
  hoverPlay?: boolean;
  /** Zeigt Steuerungselemente an */
  showControls?: boolean;
  /** Callback wenn Animation endet (nur bei loop=false) */
  onComplete?: () => void;
  /** Callback fuer Animationssegmente */
  onSegmentStart?: (segmentIndex: number) => void;
}

export interface LottiePreviewWithDataProps extends Omit<LottiePreviewProps, 'animationData'> {
  /** AnimationElement aus der Bibliothek */
  animation: AnimationElement;
}

// ===== HAUPT-KOMPONENTE =====

/**
 * Lottie-Vorschau mit interaktiven Funktionen
 */
export function LottiePreview({
  animationData,
  loop = true,
  autoplay = true,
  speed = 1,
  className = '',
  onClick,
  hoverPlay = false,
  showControls = false,
  onComplete,
}: LottiePreviewProps) {
  const { t } = useTranslation(['common']);
  // Referenz zur Lottie-Instanz
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);

  // Interner Zustand
  const [isPlaying, setIsPlaying] = useState(autoplay && !hoverPlay);
  const [currentSpeed, setCurrentSpeed] = useState(speed);
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Initialisierung der Abspielgeschwindigkeit
  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(currentSpeed);
    }
  }, [currentSpeed]);

  // Hover-Play-Logik
  useEffect(() => {
    if (!hoverPlay) return;

    if (isHovered) {
      lottieRef.current?.play();
      setIsPlaying(true);
    } else {
      lottieRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isHovered, hoverPlay]);

  // Play/Pause Toggle
  const togglePlay = useCallback(() => {
    if (!lottieRef.current) return;

    if (isPlaying) {
      lottieRef.current.pause();
    } else {
      lottieRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  // Geschwindigkeit aendern
  const handleSpeedChange = useCallback((newSpeed: number) => {
    setCurrentSpeed(newSpeed);
    if (lottieRef.current) {
      lottieRef.current.setSpeed(newSpeed);
    }
  }, []);

  // Zur Startposition springen
  const goToStart = useCallback(() => {
    if (lottieRef.current) {
      lottieRef.current.goToAndStop(0, true);
      setProgress(0);
    }
  }, []);

  // Frame-Update Handler (mit lottie-react Event-Typen)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEnterFrame = useCallback((event: any) => {
    // BMEnterFrameEvent hat currentTime und totalTime
    if (event?.totalTime > 0 && event?.currentTime !== undefined) {
      setProgress((event.currentTime / event.totalTime) * 100);
    }
  }, []);

  // Animation abgeschlossen
  const handleComplete = useCallback(() => {
    if (!loop) {
      setIsPlaying(false);
      onComplete?.();
    }
  }, [loop, onComplete]);

  // Mouse Enter/Leave Handler fuer Hover-Play
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <div
      className={`lottie-preview relative ${className}`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Lottie Animation */}
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={loop}
        autoplay={autoplay && !hoverPlay}
        onEnterFrame={handleEnterFrame}
        onComplete={handleComplete}
        style={{
          width: '100%',
          height: '100%',
        }}
      />

      {/* Hover-Overlay fuer bessere Interaktion */}
      {hoverPlay && !isHovered && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded transition-opacity">
          <div className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center shadow-sm">
            <svg
              className="w-4 h-4 text-gray-700 ml-0.5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}

      {/* Steuerungselemente */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2 rounded-b">
          {/* Fortschrittsbalken */}
          <div className="w-full h-1 bg-white/30 rounded-full mb-2 overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Kontrollen */}
          <div className="flex items-center justify-between">
            {/* Play/Pause Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded transition-colors"
              aria-label={isPlaying ? t('common:actions.pause') : t('common:actions.play')}
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Reset Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToStart();
              }}
              className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded transition-colors"
              aria-label={t('common:actions.reset')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Geschwindigkeitsauswahl */}
            <div className="flex items-center gap-1">
              {[0.5, 1, 1.5, 2].map((s) => (
                <button
                  key={s}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSpeedChange(s);
                  }}
                  className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                    currentSpeed === s
                      ? 'bg-white text-gray-900'
                      : 'text-white hover:bg-white/20'
                  }`}
                  aria-label={t('common:labels.speed', { value: s })}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== WRAPPER KOMPONENTE FUER ANIMATIONELEMENT =====

/**
 * Wrapper-Komponente die ein AnimationElement aus der Bibliothek akzeptiert
 */
export function LottiePreviewFromLibrary({
  animation,
  ...props
}: LottiePreviewWithDataProps) {
  return (
    <LottiePreview
      animationData={animation.animationData}
      loop={animation.loop}
      autoplay={animation.autoplay}
      {...props}
    />
  );
}

// ===== KOMPAKTE VORSCHAU KOMPONENTE =====

export interface LottieThumbProps {
  /** Lottie Animation JSON Daten */
  animationData: object;
  /** Groesse in Pixel (Standard: 48) */
  size?: number;
  /** Zusaetzliche CSS-Klassen */
  className?: string;
  /** Click-Handler */
  onClick?: () => void;
  /** Ausgewaehlt-Status */
  selected?: boolean;
}

/**
 * Kompakte Thumbnail-Vorschau fuer Animationen
 * Ideal fuer Auswahllisten und Bibliotheks-Browser
 */
export function LottieThumbnail({
  animationData,
  size = 48,
  className = '',
  onClick,
  selected = false,
}: LottieThumbProps) {
  const [isHovered, setIsHovered] = useState(false);
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);

  // Hover-Steuerung: Animation startet nur bei Hover
  useEffect(() => {
    if (isHovered) {
      lottieRef.current?.play();
    } else {
      lottieRef.current?.pause();
      lottieRef.current?.goToAndStop(0, true);
    }
  }, [isHovered]);

  return (
    <div
      className={`
        lottie-thumbnail relative flex items-center justify-center
        rounded-lg overflow-hidden cursor-pointer transition-all
        ${selected ? 'ring-2 ring-indigo-500 ring-offset-2' : 'hover:ring-2 hover:ring-gray-300'}
        ${className}
      `}
      style={{ width: size, height: size }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={true}
        autoplay={false}
        style={{
          width: '100%',
          height: '100%',
        }}
      />

      {/* Hover-Indikator */}
      {!isHovered && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/5">
          <div className="w-5 h-5 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
            <svg
              className="w-3 h-3 text-gray-600 ml-0.5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}

      {/* Ausgewaehlt-Indikator */}
      {selected && (
        <div className="absolute top-1 right-1 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
        </div>
      )}
    </div>
  );
}

// ===== INLINE PLAYER KOMPONENTE =====

export interface LottieInlinePlayerProps {
  /** Lottie Animation JSON Daten */
  animationData: object;
  /** Breite (Standard: 100%) */
  width?: number | string;
  /** Hoehe (Standard: auto) */
  height?: number | string;
  /** Animation wiederholen */
  loop?: boolean;
  /** Automatisch abspielen */
  autoplay?: boolean;
  /** Hintergrundfarbe */
  backgroundColor?: string;
  /** Border-Radius */
  borderRadius?: number;
  /** Bei Klick abspielen/pausieren */
  clickToToggle?: boolean;
}

/**
 * Einfacher Inline-Player ohne komplexe Steuerung
 * Fuer Einbettung in den Canvas oder andere Bereiche
 */
export function LottieInlinePlayer({
  animationData,
  width = '100%',
  height = 'auto',
  loop = true,
  autoplay = true,
  backgroundColor = 'transparent',
  borderRadius = 0,
  clickToToggle = false,
}: LottieInlinePlayerProps) {
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const [isPlaying, setIsPlaying] = useState(autoplay);

  const handleClick = useCallback(() => {
    if (!clickToToggle) return;

    if (isPlaying) {
      lottieRef.current?.pause();
    } else {
      lottieRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  }, [clickToToggle, isPlaying]);

  return (
    <div
      style={{
        width,
        height,
        backgroundColor,
        borderRadius,
        overflow: 'hidden',
        cursor: clickToToggle ? 'pointer' : 'default',
      }}
      onClick={handleClick}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={loop}
        autoplay={autoplay}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}

// ===== EXPORTS =====

export default LottiePreview;
